import { Type, type TSchema } from "typebox";
import { OrganizationRoleSchema } from "./identity.ts";
import { SUITE_PRODUCT_ORDER, SuiteProductKeySchema, type SuiteProductKey } from "./products.ts";

const InternalIdSchema = Type.String({ format: "uuid" });
const WorkOSIdSchema = Type.String({ minLength: 1, maxLength: 255 });
const UserDisplayNameSchema = Type.String({ minLength: 1, maxLength: 120 });
const OrganizationNameSchema = Type.String({ minLength: 1, maxLength: 200 });
const ProductLabelSchema = Type.String({ minLength: 1, maxLength: 120 });
const DescriptionSchema = Type.String({ minLength: 1, maxLength: 240 });
const LaunchUrlSchema = Type.String({ format: "uri", minLength: 1, maxLength: 2048 });
const UnavailableReasonSchema = Type.String({ minLength: 1, maxLength: 240 });
const SuiteStatusSchema = Type.Union([Type.Literal("entitled"), Type.Literal("grace")]);
const SameOriginPathSchema = Type.String({
  minLength: 1,
  maxLength: 2048,
  pattern: "^/(?![/\\\\])[A-Za-z0-9._~!$&'()*+,;=:@%/?#=-]*$",
});

export const SuiteUserV1Schema = Type.Object({
  id: InternalIdSchema,
  displayName: UserDisplayNameSchema,
  email: Type.Union([Type.String({ format: "email", minLength: 1, maxLength: 320 }), Type.Null()]),
  avatarUrl: Type.Union([Type.String({ format: "uri", minLength: 1, maxLength: 2048 }), Type.Null()]),
}, { additionalProperties: false });

export interface SuiteUserV1 {
  id: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
}

/**
 * Organization logo for suite chrome. Absolute Platform URLs are preferred so
 * product origins can load the asset; null means “no logo — show the name”.
 */
const SuiteOrganizationLogoUrlSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 512 }),
  Type.Null(),
]);

export const SuiteOrganizationV1Schema = Type.Object({
  id: InternalIdSchema,
  workosOrganizationId: WorkOSIdSchema,
  name: OrganizationNameSchema,
  role: OrganizationRoleSchema,
  logoUrl: SuiteOrganizationLogoUrlSchema,
}, { additionalProperties: false });

export interface SuiteOrganizationV1 {
  id: string;
  workosOrganizationId: string;
  name: string;
  role: "owner" | "admin" | "member";
  logoUrl: string | null;
}

function productSchemaFor(key: SuiteProductKey, available?: boolean): TSchema {
  const common = {
    key: Type.Literal(key),
    name: ProductLabelSchema,
    description: DescriptionSchema,
    launchUrl: LaunchUrlSchema,
  };
  if (available === true) {
    return Type.Object({
      ...common,
      available: Type.Literal(true),
      unavailableReason: Type.Null(),
    }, { additionalProperties: false });
  }
  if (available === false) {
    return Type.Object({
      ...common,
      available: Type.Literal(false),
      unavailableReason: UnavailableReasonSchema,
    }, { additionalProperties: false });
  }
  return Type.Union([productSchemaFor(key, true), productSchemaFor(key, false)]);
}

export const SuiteProductV1Schema = Type.Union([
  productSchemaFor("platform"),
  productSchemaFor("codify"),
  productSchemaFor("source"),
  productSchemaFor("radius"),
  productSchemaFor("function"),
]);

export interface SuiteProductV1 {
  key: SuiteProductKey;
  name: string;
  description: string;
  launchUrl: string;
  available: boolean;
  unavailableReason: string | null;
}

const SuiteProductTupleV1Schema = Type.Tuple([
  productSchemaFor("platform"),
  productSchemaFor("source"),
  productSchemaFor("codify"),
  productSchemaFor("function"),
  productSchemaFor("radius"),
]);

const F5AdminGlobalProductTupleV1Schema = Type.Tuple([
  productSchemaFor("platform", true),
  productSchemaFor("source", false),
  productSchemaFor("codify", false),
  productSchemaFor("function", false),
  productSchemaFor("radius", false),
]);

export const SuiteActionsV1Schema = Type.Object({
  switchOrganizationPath: SameOriginPathSchema,
  logoutPath: SameOriginPathSchema,
}, { additionalProperties: false });

export interface SuiteActionsV1 {
  switchOrganizationPath: string;
  logoutPath: string;
}

const SharedContextProperties = {
  version: Type.Literal(1),
  suiteMode: Type.Literal(true),
  user: SuiteUserV1Schema,
  organizations: Type.Array(SuiteOrganizationV1Schema, { maxItems: 100 }),
  hasMoreOrganizations: Type.Boolean(),
  actions: SuiteActionsV1Schema,
  csrfToken: Type.String({ minLength: 32, maxLength: 512 }),
};

/**
 * Product contexts always have an organization entitlement. The second branch
 * is the intentionally anonymous-to-products F5-admin global workspace.
 */
export const SuiteContextV1Schema = Type.Union([
  Type.Object({
    ...SharedContextProperties,
    currentProduct: SuiteProductKeySchema,
    activeOrganization: SuiteOrganizationV1Schema,
    suiteStatus: SuiteStatusSchema,
    products: SuiteProductTupleV1Schema,
  }, { additionalProperties: false }),
  Type.Object({
    ...SharedContextProperties,
    currentProduct: Type.Literal("platform"),
    activeOrganization: Type.Null(),
    suiteStatus: Type.Null(),
    products: F5AdminGlobalProductTupleV1Schema,
  }, { additionalProperties: false }),
]);

export interface SuiteContextV1 {
  version: 1;
  suiteMode: true;
  currentProduct: SuiteProductKey;
  user: SuiteUserV1;
  activeOrganization: SuiteOrganizationV1 | null;
  organizations: SuiteOrganizationV1[];
  hasMoreOrganizations: boolean;
  suiteStatus: "entitled" | "grace" | null;
  products: SuiteProductV1[];
  actions: SuiteActionsV1;
  csrfToken: string;
}

export { SUITE_PRODUCT_ORDER };
